import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useAddCaseDetailsProgramFlow } from './useAddCaseDetailsProgramFlow';
import { useAddCaseDetailsClientNeeds } from './useAddCaseDetailsClientNeeds';
import { useAddCaseDetailsInitialValues } from './useAddCaseDetailsInitialValues';

export const buildLegacyFields = (form, values) => {
  const buildField = (name, fallbackValue) => {
    const value = _.get(values, name);
    return {
      name,
      value: _.isUndefined(value) ? fallbackValue : value,
      onChange: (nextValue) => form.change(name, nextValue),
      onBlur: () => form.blur(name),
      onFocus: () => form.focus(name),
      active: form.getFieldState(name)?.active,
    };
  };

  const buildFieldArray = (name, itemBuilder, defaultItemValue = {}) => {
    const items = _.get(values, name, []);
    const array = (Array.isArray(items) ? items : []).map((item, index) => itemBuilder(item, index));
    array.addField = (value = defaultItemValue) => form.mutators.push(name, value);
    array.removeField = (index) => form.mutators.remove(name, index);
    return array;
  };

  return {
    case_type: buildField('case_type'),
    network: buildField('network'),
    program: buildField('program'),
    program_entry: buildField('program_entry'),
    client_needs: buildField('client_needs'),
    isOONCase: buildField('isOONCase', false),
    service_case: {
      description: buildField('service_case.description'),
      primary_worker_id: buildField('service_case.primary_worker_id'),
      service_type: buildField('service_case.service_type'),
      uploadedDocuments: buildField('service_case.uploadedDocuments'),
      attachableDocuments: buildFieldArray(
        'service_case.attachableDocuments',
        (_item, index) => ({
          attached: buildField(`service_case.attachableDocuments[${index}].attached`),
          document: buildField(`service_case.attachableDocuments[${index}].document`),
        }),
        {},
      ),
      oonCase: {
        selected: buildFieldArray(
          'service_case.oonCase.selected',
          (_item, index) => ({
            group: buildField(`service_case.oonCase.selected[${index}].group`, ''),
            program: buildField(`service_case.oonCase.selected[${index}].program`),
          }),
          { group: '' },
        ),
      },
      oonGroups: buildFieldArray(
        'service_case.oonGroups',
        (_item, index) => ({
          group: buildField(`service_case.oonGroups[${index}].group`),
        }),
        {},
      ),
    },
  };
};

export const ProgramStateHandler = ({
  values,
  form,
  initializeProgramState,
  onProgramChange,
  selectedProgramId,
  shouldInitializeForm,
}) => {
  const prevProgramIdRef = useRef(selectedProgramId);

  useEffect(() => {
    const nextProgramId = values?.program?.id;

    if (!nextProgramId) {
      return;
    }

    if (nextProgramId === prevProgramIdRef.current) {
      return;
    }

    prevProgramIdRef.current = nextProgramId;
    initializeProgramState(values);

    if (!selectedProgramId && shouldInitializeForm) {
      onProgramChange(values.program, form, values);
    }
  }, [values, form, initializeProgramState, onProgramChange, selectedProgramId, shouldInitializeForm]);

  return null;
};

ProgramStateHandler.propTypes = {
  values: PropTypes.object,
  form: PropTypes.object,
  initializeProgramState: PropTypes.func.isRequired,
  onProgramChange: PropTypes.func.isRequired,
  selectedProgramId: PropTypes.string,
  shouldInitializeForm: PropTypes.bool,
};

ProgramStateHandler.defaultProps = {
  values: undefined,
  form: undefined,
  selectedProgramId: undefined,
  shouldInitializeForm: false,
};

export const FormValuesSync = ({ values, onChange }) => {
  const prevValuesRef = useRef();

  useEffect(() => {
    if (!_.isEqual(values, prevValuesRef.current)) {
      prevValuesRef.current = values;
      onChange(values);
    }
  }, [values, onChange]);

  return null;
};

FormValuesSync.propTypes = {
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

FormValuesSync.defaultProps = {
  values: undefined,
};

export const useAddCaseDetailsController = (props) => {
  const {
    assistanceRequest,
    canPaginateNetworkGroups,
    caseDetailsValues,
    clientNeedsFromQuery,
    contactDocuments,
    contactId,
    defaultProgram,
    fetchCoreGroupForms,
    fetchProvidersUserCore,
    fetchGroupsUsersFromProgram,
    group,
    groupForms,
    groupId,
    groupsPrograms,
    hasGroupLicense,
    hasNetworkLicense,
    multipleClientNeedsFlag,
    networks,
    oonProgram,
    paginateSearchNetworkGroups,
    searchOONGroups,
    shouldInitializeForm,
    user,
  } = props;

  const [showBrowse, setShowBrowse] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);

  const registerField = useCallback(() => {}, []);
  const triggerBrowse = useCallback(() => {
    setShowBrowse((prev) => !prev);
  }, []);

  const { initialValues } = useAddCaseDetailsInitialValues({
    assistanceRequest,
    caseDetailsValues,
    contactDocuments,
    contactId,
    defaultProgram,
    hasGroupLicense,
    hasNetworkLicense,
    networks,
    shouldInitializeForm,
  });

  const {
    cancel,
    onCreateCaseDetails,
    personConditions,
    showPersonCondition,
    validateClientNeedsField,
  } = useAddCaseDetailsClientNeeds({
    assistanceRequest,
    clientNeedsFromQuery,
    contactId,
    groupForms,
    multipleClientNeedsFlag,
  });

  const programFlow = useAddCaseDetailsProgramFlow({
    assistanceRequest,
    canPaginateNetworkGroups,
    defaultProgram,
    fetchCoreGroupForms,
    fetchProvidersUserCore,
    fetchGroupsUsersFromProgram,
    group,
    groupId,
    groupsPrograms,
    hasNetworkLicense,
    networks,
    oonProgram,
    paginateSearchNetworkGroups,
    searchOONGroups,
    shouldInitializeForm,
    user,
    buildLegacyFields,
  });

  return {
    cancel,
    ...programFlow,
    disableNextButton,
    initialValues,
    onCreateCaseDetails,
    personConditions,
    registerField,
    setDisableNextButton,
    showBrowse,
    showPersonCondition,
    triggerBrowse,
    validateClientNeedsField,
  };
};
